// src/screens/sales/document/components/form/Form.tsx
import { SetStateAction } from 'react'
import { Button } from '../../../../../common/components/button/Button'
import { Input } from '../../../../../common/components/input/Input'
import { Textarea } from '../../../../../common/components/textarea/Textarea'
import { Toggle } from '../../../../../common/components/newToggle/Toggle'
import { SearchSelect } from '../../../../../common/components/searchSelect/SearchSelect'
import { SearchGroupSelect } from '../../../../../common/components/searchGroupSelect/SearchGroupSelect'
import { FileUpload } from '../../../../../common/components/fileUpload/FileUpload'
import { DocumentInterface } from '../../../../../common/types/interfaces'
import { EntityType } from '../../../../../common/types/enums'
import classes from './Form.module.sass'
import { useForm } from './hooks/useForm'

type Props = {
  edit: boolean
  data: DocumentInterface
  setData: React.Dispatch<SetStateAction<DocumentInterface>>
  getList: () => void
  setModalActive: React.Dispatch<SetStateAction<boolean>>
  el?: DocumentInterface
}

export const Form = ({ edit, data, setData, getList, setModalActive, el }: Props) => {
  const {
    formAction,
    inputHandler,
    setDirty,
    getValidationResult,
    disabled,
    files,
    pairs,
    setFiles,
    setPairs,
    linkRows,
    addLinkRow,
    removeLinkRow,
    updateLinkRow,
    changeEntityType,
    getOptionsForRow,
    getTypeOptionsForRow,
    isRowInvalid,
    canAddLinkRow,
    multipleSearchSelectHandler,
    multipleSearchSelectRemoveHandler,
    sortOrderHandler,
  } = useForm(data, setData, edit, getList, setModalActive, el)

  return (
    <div className={classes.container}>
      <div className={classes.title}>
        {data.id ? 'редактирование документа' : 'добавление документа'}
      </div>

      <Input
        type={'text'}
        fieldName={'name'}
        value={data.name}
        placeHolder={'Название документа'}
        onChangeHandler={inputHandler}
        itemValidationResult={getValidationResult('name')}
        onBlur={setDirty}
      />

      <Textarea
        type={'textarea'}
        fieldName={'description'}
        value={data.description || ''}
        placeHolder="Описание документа"
        rows={3}
        cols={20}
        onChangeHandler={inputHandler}
        itemValidationResult={getValidationResult('description')}
        onBlur={setDirty}
      />

      <FileUpload
        files={files}
        setFiles={setFiles}
        pairs={pairs}
        setPairs={setPairs}
        min_length={edit ? 0 : 1}
        length={1}
        extensions={['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'jpg', 'jpeg', 'png', 'webp']}
        maxVideoCount={0}
      />

      <div className={classes.toggleContainer}>
        <Toggle
          onChange={() => setData((prev) => ({ ...prev, isPrivate: !prev.isPrivate }))}
          checked={data.isPrivate}
        />
        <div>Приватный</div>
      </div>

      <div className={classes.sectionTitle}>Привязки</div>

      {linkRows.map((row) => {
        const isCluster = row.entityType === EntityType.CLUSTER
        const rowInvalid = isRowInvalid(row)
        return (
          <div
            key={row.uid}
            className={classes.linkRow}
            style={{ borderLeft: rowInvalid ? 'solid 2px rgb(254, 111, 103, 0.8)' : '' }}
          >
            <SearchSelect
              onBlur={setDirty}
              fieldName={`type-${row.uid}`}
              options={getTypeOptionsForRow(row)}
              value={row.entityType}
              onChangeHandler={(value) => changeEntityType(row.uid, Number(value) as EntityType)}
              type="text"
              placeholder="Тип"
            />

            {!isCluster && (
              <>
                <div className={classes.toggleContainer}>
                  <Toggle
                    onChange={() => updateLinkRow(row.uid, { isAll: !row.isAll, entityIds: [] })}
                    checked={row.isAll}
                  />
                  <div>Все</div>
                </div>

                {!row.isAll && (
                  <SearchGroupSelect
                    onBlur={setDirty}
                    fieldName={row.uid}
                    options={getOptionsForRow(row)}
                    value={row.entityIds}
                    onChangeHandler={multipleSearchSelectHandler}
                    onRemoveHandler={multipleSearchSelectRemoveHandler}
                    type="array"
                    placeholder="Выберите сущности"
                  />
                )}
              </>
            )}

            <Input
              type={'text'}
              fieldName={`sort-${row.uid}`}
              value={row.sortOrder}
              placeHolder={'Порядок'}
              onChangeHandler={(value) => sortOrderHandler(row.uid, value)}
            />

            <button
              type="button"
              className={classes.removeBtn}
              onClick={() => removeLinkRow(row.uid)}
            >
              ×
            </button>

            {rowInvalid && (
              <div className={classes.rowError}>Выберите сущности или включите «Все»</div>
            )}
          </div>
        )
      })}

      <Button text="Добавить привязку" onClick={addLinkRow} disabled={!canAddLinkRow} />

      <Button text={edit ? 'Сохранить' : 'Добавить'} onClick={formAction} disabled={disabled} />
    </div>
  )
}
